import { apiClient } from '../../../../lib/axios';
import type {
  FilterOptionsResponse,
  UsersFilterOptionsResponse,
  FilterOptionsApiParams,
  DepartmentOptionsResponse,
  RegionOptionsResponse,
  DepotOptionsResponse,
  JobRoleOptionsResponse,
  CascadeParams,
} from './types';

const cascadeQuery = (c?: CascadeParams) => ({
  region: c?.regionIds?.length ? c.regionIds : undefined,
  depot: c?.depotIds?.length ? c.depotIds : undefined,
  department: c?.departmentIds?.length ? c.departmentIds : undefined,
  job_role: c?.jobRoleIds?.length ? c.jobRoleIds : undefined,
  age_range: c?.ageRanges?.length ? c.ageRanges : undefined,
  sex: c?.sex?.length ? c.sex : undefined,
  shift_pattern: c?.shiftPatterns?.length ? c.shiftPatterns : undefined,
});

export const filterOptionsApi = {
  getDepartments: async (
    clientId?: string | null,
    bypassAnonymisation?: boolean,
    withData?: boolean,
    regionIds?: string[],
  ): Promise<DepartmentOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/departments', {
      params: clientId
        ? {
            client_id: clientId,
            bypass_anonymisation: bypassAnonymisation || undefined,
            with_data: withData || undefined,
            region: regionIds && regionIds.length > 0 ? regionIds : undefined,
          }
        : undefined,
    });
    return response.data;
  },

  getRegions: async (clientId?: string | null): Promise<RegionOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/regions', {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  getDepots: async (clientId?: string | null, cascade?: CascadeParams): Promise<DepotOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/depots', {
      params: { client_id: clientId || undefined, region: cascade?.regionIds?.length ? cascade.regionIds : undefined },
    });
    return response.data;
  },

  getShiftPatterns: async (clientId?: string | null, cascade?: CascadeParams): Promise<FilterOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/shift-patterns', {
      params: { client_id: clientId || undefined, ...cascadeQuery(cascade) },
    });
    return response.data;
  },

  getAgeRanges: async (clientId?: string | null, cascade?: CascadeParams): Promise<FilterOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/age-ranges', {
      params: { client_id: clientId || undefined, ...cascadeQuery(cascade) },
    });
    return response.data;
  },

  getSexOptions: async (clientId?: string | null, cascade?: CascadeParams): Promise<FilterOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/sex', {
      params: { client_id: clientId || undefined, ...cascadeQuery(cascade) },
    });
    return response.data;
  },

  getJobRoles: async (
    clientId?: string | null,
    params?: { regionIds?: string[]; depotIds?: string[]; departmentIds?: string[] },
  ): Promise<JobRoleOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/job-roles', {
      params: {
        client_id: clientId || undefined,
        region: params?.regionIds?.length ? params.regionIds : undefined,
        depot: params?.depotIds?.length ? params.depotIds : undefined,
        department: params?.departmentIds?.length ? params.departmentIds : undefined,
      },
    });
    return response.data;
  },

  getUsers: async (
    params?: FilterOptionsApiParams & { client_id?: string | null },
  ): Promise<UsersFilterOptionsResponse> => {
    const response = await apiClient.get('/admin/programme-profile/filter-options/users', {
      params: {
        search: params?.search || '',
        limit: params?.limit || 20,
        offset: params?.offset || 0,
        user_ids: params?.user_ids && params.user_ids.length > 0 ? params.user_ids : undefined,
        client_id: params?.client_id || undefined,
        bypass_anonymisation: params?.bypass_anonymisation || undefined,
        start_date: params?.startDate || undefined,
        end_date: params?.endDate || undefined,
        reliable_data: params?.reliableDataOnly || undefined,
        ...cascadeQuery(params),
      },
    });
    return response.data;
  },
};
